"use client";

import { Radio } from "lucide-react";
import Link from "@/components/ui/Link";
import { useEffect, useRef } from "react";
import { EmptyState } from "@/components/ui/EmptyState";
import { streamGridClass } from "@/components/video/VideoGrid";
import { compact } from "@/lib/format";
import { hasFilters, type StreamQuery, streamsHref } from "@/lib/stream-filters";
import { StreamCard, StreamCardSkeleton } from "./StreamCard";
import { type StreamList, useLiveStreams } from "./useLiveStreams";
import { categoryHref } from "@/lib/links";
import { useUi } from "@/components/ui/use-ui";
import { fmt, rich } from "@/lib/locales/fmt";

/**
 * Every live stream matching the filters (`params`, a /api/streams query string), four across on a
 * desktop; more load as you scroll, ended ones drop out.
 */
export function StreamsGrid({
  initial,
  pageSize,
  params = "",
  query = { platform: null },
  channel = null,
}: {
  initial: StreamList;
  pageSize: number;
  params?: string;
  query?: StreamQuery;
  /** The Telegram channel's link, offered beside the count. */
  channel?: string | null;
}) {
  const t = useUi();
  const { platform, tag } = query;
  const { items, total, hasMore, loading, error, loadMore } = useLiveStreams(initial, pageSize, params);
  const sentinel = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = sentinel.current;
    if (!el || !hasMore || error) return;
    const io = new IntersectionObserver((entries) => entries[0]?.isIntersecting && loadMore(pageSize), { rootMargin: "1400px 0px" });
    io.observe(el);
    return () => io.disconnect();
  }, [hasMore, error, loadMore, pageSize, items.length]);

  if (!items.length && hasFilters(query))
    return (
      <EmptyState icon={<Radio size={26} />} title={t.cams.noMatch} action={{ href: streamsHref({ platform }), label: t.cams.clearFilters }}>
        {t.cams.tryAnother}
        {/* Old links to a video category (/?tag=…) land here since the home page became live streams. */}
        {tag && (
          <>
            {" "}
            {rich(t.cams.orWatch, {
              link: (
                <Link href={categoryHref(tag)} className="text-soft underline underline-offset-2 hover:text-fg">
                  {fmt(t.cams.videoTag, { tag })}
                </Link>
              ),
            })}
          </>
        )}
      </EmptyState>
    );
  if (!items.length)
    return platform ? (
      <EmptyState icon={<Radio size={26} />} title={fmt(t.cams.nobodyLiveOn, { name: platform.name })} action={{ href: "/", label: t.cams.seeAllPlatforms }}>
        {fmt(t.cams.platformShowUpHint, { name: platform.name })}
      </EmptyState>
    ) : (
      <EmptyState icon={<Radio size={26} />} title={t.cams.nobodyLive} action={{ href: "/videos", label: t.cams.watchVideos }}>
        {t.cams.showUpHint}
      </EmptyState>
    );

  return (
    <>
      <p className="-mt-1 mb-5 text-[14px] text-muted">
        {fmt(t.cams.liveNowCount, { n: compact(total) })}
        {platform ? fmt(t.cams.onPlatform, { name: platform.name }) : ""} ·{" "}
        {fmt(t.cams.opensOn, { site: platform ? platform.name : t.cams.streamerSite })}
        {channel && (
          <>
            {" · "}
            <a href={channel} target="_blank" rel="noopener" data-stat="telegram:count" className="text-brand-soft hover:underline hover:underline-offset-2">
              {t.cams.newOnTelegram}
            </a>
          </>
        )}
      </p>
      <div className={streamGridClass}>
        {items.map((s, i) => (
          <StreamCard key={s.id} stream={s} eager={i < 8} priority={i < 4} />
        ))}
        {loading && Array.from({ length: 7 }, (_, i) => <StreamCardSkeleton key={`s${i}`} />)}
      </div>
      <div ref={sentinel} className="h-px" />
      {error && (
        <div className="mt-8 flex justify-center">
          <button type="button" onClick={() => loadMore(pageSize)} className="chip h-10 rounded-full px-5 text-[14px] text-soft">
            {t.cams.loadMoreFailed}
          </button>
        </div>
      )}
    </>
  );
}
